/**
 * Event names, the way a person picks them — for the alert rule form.
 *
 * A rule matches an event by NAME: its event type (the model, e.g. "fire_smoke") or sub-type
 * (one kind of event from it, e.g. "fire_detected"). Models spell these differently between
 * versions, and a rule written for one spelling goes quiet when the model changes it. So a rule
 * lists every spelling it knows: picking "Fire & smoke" fills in the names fire events are sent
 * under, picking the sub-event "Fire detected" fills in that one's — and the operator can edit the
 * list. The command-center compares names ignoring case, spaces, "-" and "." (alertEvaluator
 * normType), so "fire-detect" and "fire detect" already match "fire_detect".
 *
 * `template` is a suggested alert message for the family (variables: see MESSAGE_VARIABLES).
 */
import type { EventCatalogEntry } from './ruleConditionsModel';

export interface EventChoice {
  value: string;
  label: string;
  /** Every name this choice is known by — what the rule will match. */
  aliases: string[];
}

export interface EventFamily extends EventChoice {
  subs: EventChoice[];
  template: string;
}

const T_CHECK = 'Check the feed and escalate if confirmed.';

export const EVENT_FAMILIES: EventFamily[] = [
  {
    value: 'fire_smoke', label: 'Fire & smoke',
    aliases: ['fire_smoke', 'fire', 'smoke', 'fire and smoke', 'fire_event', 'fire_alert', 'fire_detect', 'fire_detected', 'smoke_detected'],
    subs: [
      { value: 'fire_detected', label: 'Fire detected', aliases: ['fire_detected', 'fire_detect', 'fire-detect', 'fire_detection', 'fire_alert', 'fire_catch', 'flame_detected'] },
      { value: 'smoke_detected', label: 'Smoke detected', aliases: ['smoke_detected', 'smoke_detect', 'smoke-detect', 'smoke_detection', 'smoke_alert'] },
    ],
    template: '${alert_name} detected — flames or smoke were seen on camera ${camera_name} at ${time}. ' + T_CHECK,
  },
  {
    value: 'intrusion', label: 'Intrusion',
    aliases: ['intrusion', 'intrusion_detected', 'zone_breach', 'trespass', 'intruder'],
    subs: [
      { value: 'zone_breach', label: 'Entered a restricted zone', aliases: ['zone_breach', 'zone-breach', 'zone_intrusion', 'restricted_area', 'zone_entry'] },
    ],
    template: '${alert_name} — someone entered a restricted area on camera ${camera_name} at ${time}. ' + T_CHECK,
  },
  {
    value: 'person', label: 'Person',
    aliases: ['person', 'person_detected', 'human', 'people'],
    subs: [
      { value: 'crossing', label: 'Crossed a line', aliases: ['crossing', 'line_crossing', 'person_crossing', 'line_cross'] },
      { value: 'loitering', label: 'Loitering', aliases: ['loitering', 'loiter', 'person_loitering'] },
    ],
    template: '${alert_name} on camera ${camera_name} at ${time}.',
  },
  {
    value: 'person_count', label: 'Too many people',
    aliases: ['person_count', 'person_counting', 'people_count', 'crowd'],
    subs: [
      { value: 'person_counting', label: 'Count over the limit', aliases: ['person_counting', 'overcrowding', 'crowd_count', 'occupancy_exceeded'] },
    ],
    template: '${alert_name} — too many people on camera ${camera_name} at ${time}.',
  },
  {
    value: 'face', label: 'Face recognition',
    aliases: ['face', 'face_recognition', 'face_recognized'],
    subs: [
      { value: 'face_match', label: 'Known face', aliases: ['face_match', 'face_matched', 'face_recognized', 'known_face'] },
      { value: 'unknown_face', label: 'Unknown face', aliases: ['unknown_face', 'face_unknown', 'stranger'] },
    ],
    template: '${alert_name} — a face was recognised on camera ${camera_name} at ${time}.',
  },
  {
    value: 'vehicle_crossing', label: 'Vehicle',
    aliases: ['vehicle_crossing', 'vehicle', 'anpr', 'number_plate'],
    subs: [
      { value: 'anpr_inout_match', label: 'Number plate seen', aliases: ['anpr_inout_match', 'plate_match', 'anpr_match', 'plate_read'] },
    ],
    template: '${alert_name} — vehicle seen on camera ${camera_name} at ${time}.',
  },
  {
    value: 'ppe_violation', label: 'Safety gear (PPE)',
    aliases: ['ppe_violation', 'ppe', 'safety_gear', 'no_helmet', 'no_vest'],
    subs: [
      { value: 'no_helmet', label: 'No helmet', aliases: ['no_helmet', 'helmet_missing', 'without_helmet'] },
      { value: 'no_vest', label: 'No safety vest', aliases: ['no_vest', 'vest_missing', 'without_vest'] },
    ],
    template: '${alert_name} — someone without safety gear on camera ${camera_name} at ${time}.',
  },
  {
    value: 'theft', label: 'Theft',
    aliases: ['theft', 'shoplifting', 'stealing'],
    subs: [
      { value: 'shoplifting', label: 'Shoplifting', aliases: ['shoplifting', 'shop_lifting', 'theft_detected'] },
    ],
    template: '${alert_name} — possible theft on camera ${camera_name} at ${time}. ' + T_CHECK,
  },
  {
    value: 'ocr', label: 'Text / number reading (OCR)',
    aliases: ['ocr', 'ocr_detection', 'text_detection', 'ocr_read', 'ocr_text', 'panel_reading', 'meter_reading'],
    subs: [
      { value: 'panel_reading', label: 'Panel / meter reading', aliases: ['panel_reading', 'meter_reading', 'gauge_reading', 'display_reading'] },
      { value: 'ocr_plate', label: 'Number plate read', aliases: ['ocr_plate', 'plate_read', 'plate_detected'] },
    ],
    template: '${alert_name} — read "${read_text}" on camera ${camera_name} at ${time}. ' + T_CHECK,
  },
  {
    value: 'belt_movement', label: 'Conveyor belt',
    aliases: ['belt_movement', 'conveyor', 'conveyor_belt', 'belt'],
    subs: [
      { value: 'no_belt_movement', label: 'Belt stopped', aliases: ['no_belt_movement', 'belt_stopped', 'belt_stop', 'conveyor_stopped'] },
    ],
    template: '${alert_name} — the belt on camera ${camera_name} has stopped (at ${time}). ' + T_CHECK,
  },
  {
    value: 'fall', label: 'Fall',
    aliases: ['fall', 'fall_detected', 'fall_detection', 'person_fall'],
    subs: [
      { value: 'fall_detected', label: 'Person fell', aliases: ['fall_detected', 'fall-detected', 'person_fall', 'slip_fall'] },
    ],
    template: '${alert_name} — a person fell on camera ${camera_name} at ${time}. Send help and check the feed.',
  },
];

export const GENERIC_TEMPLATE = '${alert_name} on camera ${camera_name} at ${time}. ' + T_CHECK;

/** The variables a message can use, besides the event's own fields — what the command-center fills in. */
export const MESSAGE_VARIABLES: { name: string; label: string; sample: string }[] = [
  { name: 'alert_name', label: 'Alert name', sample: 'Fire Detected' },
  { name: 'camera_name', label: 'Camera', sample: 'Store Room 2' },
  { name: 'event', label: 'Event', sample: 'fire_detected' },
  { name: 'sub_event', label: 'Sub event', sample: 'fire_detected' },
  { name: 'model', label: 'Model', sample: 'fire_smoke' },
  { name: 'severity', label: 'Severity', sample: 'CRITICAL' },
  { name: 'confidence', label: 'How sure', sample: '87%' },
  { name: 'time', label: 'Time', sample: '14:32' },
  { name: 'date', label: 'Date', sample: '2 Nov 2026' },
  { name: 'read_text', label: 'Text the AI read (OCR)', sample: 'PALLET 42' },
];

const norm = (t: string) => String(t || '').trim().toLowerCase().replace(/[\s.-]+/g, '_');

/** Other spellings of one name: fire_detect → fire-detect, fire detect, fire_detected. */
export function spellings(name: string): string[] {
  const t = String(name || '').trim();
  if (!t) return [];
  const base = norm(t);
  const out = [t, base, base.replace(/_/g, '-'), base.replace(/_/g, ' ')];
  if (/_detected$/.test(base)) out.push(base.replace(/_detected$/, '_detect'), base.replace(/_detected$/, '_detection'));
  else if (/_detect$/.test(base)) out.push(`${base}ed`, `${base}ion`);
  return [...new Set(out)];
}

/** Unique, case/separator-insensitively: keeps the first spelling of each. */
export function uniqueNames(names: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const n of names.map((x) => String(x).trim()).filter(Boolean)) {
    const k = n === '*' ? '*' : norm(n);
    if (!seen.has(k)) { seen.add(k); out.push(n); }
  }
  return out;
}

/** The families, plus any event type the AI has sent that is none of them (with its sub-types). */
export function eventChoices(catalog: EventCatalogEntry[]): EventFamily[] {
  const known = (t: string) => EVENT_FAMILIES.find((f) => f.aliases.some((a) => norm(a) === norm(t)) || norm(f.value) === norm(t));
  const extra = new Map<string, EventFamily>();
  const families = EVENT_FAMILIES.map((f) => ({ ...f, subs: [...f.subs] }));
  for (const e of catalog || []) {
    const fam = known(e.event_type);
    const target = fam ? families.find((f) => f.value === fam.value)! : extra.get(e.event_type) || (() => {
      const f: EventFamily = { value: e.event_type, label: e.event_type.replace(/[_-]+/g, ' '), aliases: spellings(e.event_type), subs: [], template: GENERIC_TEMPLATE };
      extra.set(e.event_type, f);
      return f;
    })();
    if (e.event_subtype && !target.subs.some((s) => s.aliases.some((a) => norm(a) === norm(e.event_subtype)))) {
      target.subs.push({ value: e.event_subtype, label: e.event_subtype.replace(/[_-]+/g, ' '), aliases: spellings(e.event_subtype) });
    }
  }
  return [...families, ...extra.values()];
}

/**
 * Which family and sub-event a rule's names point at — to show the pickers on an existing rule.
 * A sub-event when every name is one of its aliases; else a family when any name is one of its.
 */
export function inferChoice(tokens: string[], families: EventFamily[]): { family: EventFamily | null; sub: EventChoice | null } {
  const names = tokens.filter((t) => t !== '*').map(norm);
  if (!names.length) return { family: null, sub: null };
  for (const f of families) {
    for (const s of f.subs) {
      const set = new Set(s.aliases.map(norm));
      if (names.every((n) => set.has(n))) return { family: f, sub: s };
    }
  }
  const family = families.find((f) => names.some((n) => f.aliases.map(norm).includes(n) || norm(f.value) === n)) || null;
  return { family, sub: null };
}

/** Fill ${var} / {var} with sample values — the preview under the message box. */
export function previewMessage(template: string, extra: Record<string, string> = {}): string {
  const samples: Record<string, string> = Object.fromEntries(MESSAGE_VARIABLES.map((v) => [v.name, v.sample]));
  samples.cam_name = samples.camera_name;
  samples.event_type = samples.event;
  return String(template || '').replace(/\$?\{\s*([\w.]+)\s*\}/g, (_m, k) => extra[k] ?? samples[k] ?? `‹${k}›`);
}
